import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Copy, ExternalLink, Search } from "lucide-react";
import { BrandDetails } from "../../../../types/add-brand-types";
import { useLeads } from "../../../../hooks/use-leads";
import useBrandApi from "../../../../api/use-add-brand-api";
import Shimmer from "../../../shimmer/Shimmer";
import Toast from "../../../uiComponents/Toast";
import "./leads.css";
import "../History.css";

interface LeadsProps {
  brandDetails: BrandDetails;
  refreshBrandDetails?: () => void;
}

const Leads: React.FC<LeadsProps> = ({
  brandDetails,
  refreshBrandDetails,
}) => {
  const { updateBrand } = useBrandApi();
  const {
    isLoading,
    page,
    totalPages,
    totalLeads,
    leadItems,
    hasLeads,
    isGenerating,
    generateProspects,
    goToPrevPage,
    goToNextPage,
  } = useLeads(brandDetails);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [leadPreferences, setLeadPreferences] = useState(
    brandDetails.marketing_strategy?.lead_preferences || ""
  );
  const [isSavingPreference, setIsSavingPreference] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    isVisible: boolean;
  } | null>(null);

  useEffect(() => {
    setLeadPreferences(brandDetails.marketing_strategy?.lead_preferences || "");
  }, [brandDetails.id, brandDetails.marketing_strategy?.lead_preferences]);

  const handleSavePreferences = async () => {
    if (!brandDetails.id || isSavingPreference) return;

    setIsSavingPreference(true);
    const response = await updateBrand(
      {
        lead_preferences: leadPreferences.trim(),
      },
      brandDetails.id
    );
    setIsSavingPreference(false);

    if (response?.status === "success") {
      setToast({
        message: "Lead preference saved.",
        type: "success",
        isVisible: true,
      });
      refreshBrandDetails?.();
      setIsConfigOpen(false);
      return;
    }

    setToast({
      message: "Could not save lead preference.",
      type: "error",
      isVisible: true,
    });
  };

  const handleGenerateProspects = async () => {
    const response = await generateProspects();
    if (!response) {
      setToast({
        message: "Could not generate prospects.",
        type: "error",
        isVisible: true,
      });
      return;
    }

    if (response.data.status === "skip") {
      const retryHours = response.data.retry_after_seconds
        ? Math.ceil(response.data.retry_after_seconds / 3600)
        : null;
      setToast({
        message:
          response.data.reason === "lead_generation_recently_ran" && retryHours
            ? `Prospects were refreshed recently. Try again in about ${retryHours}h.`
            : "Prospect generation is not available for this plan.",
        type: "info",
        isVisible: true,
      });
      return;
    }

    setToast({
      message: `Generated ${response.data.saved} new prospect${
        response.data.saved === 1 ? "" : "s"
      }.`,
      type: "success",
      isVisible: true,
    });
  };

  const handleCopyDm = async (message: string) => {
    if (!message) return;
    await navigator.clipboard.writeText(message);
    setToast({
      message: "Suggested DM copied.",
      type: "success",
      isVisible: true,
    });
  };

  const leadsShimmer = () => {
    return (
      <table className="history-table">
        <thead>
          <tr>
            <th>
              <Shimmer width={100} />
            </th>
            <th>
              <Shimmer width={120} />
            </th>
            <th>
              <Shimmer width={80} />
            </th>
            <th>
              <Shimmer width={80} />
            </th>
            <th>
              <Shimmer width={60} />
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 4 }).map((_, i) => (
            <tr key={i}>
              <td>
                <Shimmer width={180} />
              </td>
              <td>
                <Shimmer width={260} />
              </td>
              <td>
                <Shimmer width={180} />
              </td>
              <td>
                <Shimmer width={120} />
              </td>
              <td>
                <Shimmer width={60} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="history-tab">
      <div className="history-container" style={{ marginTop: 0 }}>
        <div
          className="history-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="leads-header-title">
            <span>Prospects ({totalLeads})</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              className="leads-generate-trigger"
              onClick={handleGenerateProspects}
              disabled={isGenerating || isLoading}
            >
              {isGenerating ? "Generating..." : "Generate prospects"}
            </button>
            <button
              type="button"
              className="leads-configure-trigger"
              onClick={() => setIsConfigOpen((prev) => !prev)}
            >
              {isConfigOpen ? "Close" : "Configure"}
            </button>
            {totalLeads > 0 && totalPages > 0 && (
              <div className="history-pagination">
                <button
                  type="button"
                  onClick={goToPrevPage}
                  disabled={page <= 1 || isLoading}
                  aria-label="Previous page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    background: page <= 1 || isLoading ? "#f9fafb" : "#fff",
                    color: page <= 1 || isLoading ? "#9ca3af" : "#374151",
                    cursor: page <= 1 || isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={goToNextPage}
                  disabled={page >= totalPages || isLoading}
                  aria-label="Next page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    background:
                      page >= totalPages || isLoading ? "#f9fafb" : "#fff",
                    color:
                      page >= totalPages || isLoading ? "#9ca3af" : "#374151",
                    cursor:
                      page >= totalPages || isLoading
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        {isConfigOpen && (
          <div className="leads-preference-panel">
            <div className="leads-preference-header">
              <h3>Lead search preference</h3>
              <p>
                Add the kind of companies, roles, regions, or signals you want
                us to prioritize when finding prospect targets for this brand.
              </p>
            </div>
            <textarea
              className="leads-preference-input"
              value={leadPreferences}
              onChange={(event) => setLeadPreferences(event.target.value)}
              placeholder="Example: prioritize SaaS startups and agencies in the US, avoid enterprise companies and job boards."
              rows={4}
            />
            <div className="leads-preference-actions">
              <button
                type="button"
                className="leads-preference-secondary"
                onClick={() => {
                  setLeadPreferences(
                    brandDetails.marketing_strategy?.lead_preferences || ""
                  );
                  setIsConfigOpen(false);
                }}
                disabled={isSavingPreference}
              >
                Cancel
              </button>
              <button
                type="button"
                className="leads-preference-primary"
                onClick={handleSavePreferences}
                disabled={isSavingPreference}
              >
                {isSavingPreference ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          leadsShimmer()
        ) : !hasLeads ? (
          <div style={{ padding: "20px", color: "#6b7280" }}>
            No prospects found for this brand.
          </div>
        ) : (
          <table className="history-table">
            <thead>
              <tr>
                <th>COMPANY NAME</th>
                <th style={{ width: "26%" }}>SIGNAL</th>
                <th style={{ width: "26%" }}>WHY IT FITS</th>
                <th>SOURCE</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {leadItems.map((lead) => {
                const linkedinKeyword =
                  lead.companyName && lead.companyName !== "--"
                    ? lead.companyName
                    : lead.linkedinSearchQuery;

                return (
                  <tr key={lead.id}>
                    <td style={{ fontWeight: 600, color: "#1f2937" }}>
                      <div className="leads-company-name">{lead.companyName}</div>
                      <div className="leads-target-type">
                        {lead.targetType}
                        {lead.confidenceScore !== null && (
                          <span>{lead.confidenceScore}% fit</span>
                        )}
                      </div>
                    </td>
                    <td className="leads-company-type-cell">
                      {lead.signal}
                    </td>
                    <td className="leads-company-type-cell">
                      <div>{lead.fitReason}</div>
                      {lead.outreachAngle !== "--" && (
                        <div className="leads-outreach-angle">
                          Angle: {lead.outreachAngle}
                        </div>
                      )}
                    </td>
                    <td>
                      {lead.sourceUrl ? (
                        <a
                          href={lead.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="leads-site-link"
                        >
                          {lead.sourceTitle}
                          <ExternalLink size={14} />
                        </a>
                      ) : (
                        "--"
                      )}
                    </td>
                    <td>
                      <div className="leads-action-stack">
                        <a
                          href={`https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(
                            linkedinKeyword
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="leads-action-link"
                        >
                          <Search size={14} />
                          LinkedIn
                        </a>
                        {lead.suggestedDm && (
                          <button
                            type="button"
                            className="leads-action-link"
                            onClick={() => handleCopyDm(lead.suggestedDm)}
                          >
                            <Copy size={14} />
                            Copy DM
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {toast && toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Leads;
